import readings from './raamPerimeterRows.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { measureLine } from '../measuredLine';
import type { ReservedFootprint } from '../types';
import { obliqueRowLots, type ObliqueRow } from './obliqueRowLots';

export const RAAM_PERIMETER_ROWS = readings.rows as unknown as readonly ObliqueRow[];

/** The western/northern joins are fitted block limits, not recovered medieval parcel edges. */
export function raamPerimeterReservation(layout: BuildContext['layout']): ReservedFootprint | null {
  const find = (id: string) => layout.streets.find((s) => s.id === id),
    raam = find('street-raamsteeg'),
    north = find('street-schoorsteenvegerssteeg'),
    wall = find('lane-singel-4'),
    quay = find('quay-nieuwezijds-achterburgwal-2');
  if (!raam || !north || !wall || !quay) return null;
  const line = measureLine(
      wall.centerline,
      wall.centerline.map(() => wall.width)
    ),
    south = line.pointAt(223).point,
    west = line.pointAt(315).point,
    outer: Vec2[] = [...raam.centerline, south, west, ...[...north.centerline].reverse()];
  return { id: 'surveyed-raam-perimeter', polygon: { outer, holes: [] } };
}

export function planRaamPerimeter(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  const block = raamPerimeterReservation(ctx.layout);
  if (!block) return { lots: [], reserved: [], unresolved: [] };
  const lots = obliqueRowLots(RAAM_PERIMETER_ROWS, ctx.layout, buildable, reserved, 54_100).map(
    (lot) => ({
      ...lot,
      reconstruction: {
        ...lot.reconstruction!,
        params: {
          ...lot.reconstruction!.params,
          storeyHeight: 2.8,
          pitch: 0.85,
          frontType: 'brick' as const,
          frontColour: 'light' as const,
          backGable: lot.reconstruction!.params.frontGable ?? 'plain',
          chimneys: [],
        },
      },
    })
  );
  const built = new Set(lots.map((l) => l.reconstruction.id)),
    unresolved = RAAM_PERIMETER_ROWS.flatMap((r) => r.houses.map((h) => h.id)).filter(
      (id) => !built.has(id)
    );
  return {
    lots,
    reserved: [
      block,
      ...lots.map((l): ReservedFootprint => ({
        id: `surveyed:${l.reconstruction.id}`,
        polygon: { outer: [...l.corners], holes: [] },
      })),
    ],
    unresolved,
  };
}
